import { Injectable } from '@angular/core';
import { Actions,createEffect,ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { EMPTY, exhaustMap, firstValueFrom, from, mergeMap, of } from 'rxjs';
import { catchError, concatMap, map, switchMap } from 'rxjs/operators';
import { LeadAppLinkEntity,LeadAppLinkEntityFromBackend } from '../../../entities/mdqx/leadapp/lead-app-link.entity';
import { AppGatewayService } from '../../../services/app-gateway.service';
import { DaveActions } from '../../actions/actions';
import { BaseActionTypes } from '../../actions/base.actions';
import { LeadAppLinkActionTypes } from '../../actions/mdqx/lead-app-link.actions';
import { State } from '../../index';
import { MaterialListActionTypes } from '../../actions/material-list.actions';
import { isNotNullOrUndefined } from '../../../../helper/helper';
import { MaterialListEntityFromBackend } from '../../../entities/material-list.entity';
import { getMaterialListDictionary } from '../../selectors/material-list.selector';
import { GetTimestampFromTime } from '@mdqx/types';
import { getLeadAppLinkDictionary } from '../../selectors/mdqx/lead-app-link.selector';

const ErrorCodes = {
    Get: LeadAppLinkEntity.EntityName + ' Abrufen fehlgeschlagen',
    Add: LeadAppLinkEntity.EntityName + ' Hinzufügen fehlgeschlagen',
    Edit: LeadAppLinkEntity.EntityName + ' Bearbeiten fehlgeschlagen',
    Delete: LeadAppLinkEntity.EntityName + ' Löschen fehlgeschlagen',
};

@Injectable()
export class LeadAppLinkEffects {
    GetLeadAppLinks$  = createEffect(() => {
        return this.actions$.pipe(
            ofType(LeadAppLinkActionTypes.GetRequest),
            switchMap(({ Payload }) => {
                const requestPayload = isNotNullOrUndefined(Payload?.updatedSince) ? { UpdatedSince: Payload.updatedSince, WithDeleted: false } : { WithDeleted: false };
                return from(this.gatewayService.Request({ LeadAppLinkGet: requestPayload })).pipe(
                    mergeMap((res) => {
                        if (Object.keys(res?.Errors || {}).length === 0) {
                            if (!isNotNullOrUndefined(Payload.updatedSince)) {
                                return of(
                                    LeadAppLinkActionTypes.GetSuccess({
                                        Payload: res.LeadAppLinkGet.Links.map((val) => LeadAppLinkEntityFromBackend(val)),
                                        updateLatestUpdatedAt: true,
                                    }),
                                );
                            } else if (res.LeadAppLinkGet.Links.length) {
                                return from(firstValueFrom(this.store$.select(getLeadAppLinkDictionary))).pipe(
                                    mergeMap((LeadAppLinks) => {
                                        const filteredLeadAppLinks = res.LeadAppLinkGet.Links.filter((LeadAppLink) => {
                                            const fromState = LeadAppLinks[LeadAppLink.Id];
                                            return !fromState || GetTimestampFromTime(fromState.UpdatedAt).toString() !== LeadAppLink.UpdatedAt;
                                        });

                                        if (filteredLeadAppLinks.length) {
                                            return of(
                                                LeadAppLinkActionTypes.GetSuccess({
                                                    Payload: filteredLeadAppLinks.map((val) => LeadAppLinkEntityFromBackend(val)),
                                                    updateLatestUpdatedAt: true,
                                                }),
                                            );
                                        } else {
                                            return EMPTY;
                                        }
                                    }),
                                );
                            }
                            return EMPTY;
                        } else {
                            throw res.Errors;
                        }
                    }),
                    catchError((err) =>
                        of(
                            BaseActionTypes.ErrorAction({
                                Payload: {
                                    ToasterMessage: ErrorCodes.Get,
                                    Err: err,
                                },
                            }),
                        ),
                    ),
                );
            }),
        );
    });

    GetLeadAppLinkById$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(LeadAppLinkActionTypes.GetLinkRequestById),
            concatMap(({ Payload }) =>
                this.gatewayService
                    .Request({
                        LeadAppLinkGet: {
                            Id: Payload,
                            WithDeleted: false,
                        },
                    })
                    .then((res) => {
                        if (Object.keys(res?.Errors || {}).length === 0) {
                            return LeadAppLinkActionTypes.GetSuccess({
                                Payload: res.LeadAppLinkGet.Links.map((link) => LeadAppLinkEntityFromBackend(link)),
                                updateLatestUpdatedAt: true,
                            });
                        } else {
                            throw res.Errors;
                        }
                    })
                    .catch((err) => {
                        return LeadAppLinkActionTypes.GetFailure({
                            Payload: {
                                ToasterMessage: ErrorCodes.Get,
                                Errors: err,
                            },
                        });
                    }),
            ),
        );
    });

    AddLeadAppLink$ = createEffect(() =>
        this.actions$.pipe(
            ofType(LeadAppLinkActionTypes.AddRequest),
            concatMap(({ Payload }) =>
                this.gatewayService
                    .Request({ LeadAppLinkAdd: Payload })
                    .then((res) => {
                        if (Object.keys(res?.Errors || {}).length === 0) {
                            return LeadAppLinkActionTypes.AddSuccess({
                                Payload: LeadAppLinkEntityFromBackend(res.LeadAppLinkAdd.Link),
                            });
                        } else {
                            throw res.Errors;
                        }
                    })
                    .catch((err) => {
                        return LeadAppLinkActionTypes.AddFailure({
                            Payload: {
                                ToasterMessage: ErrorCodes.Add,
                                Errors: err,
                            },
                        });
                    }),
            ),
        ),
    );

    EditLeadAppLink$ = createEffect(() =>
        this.actions$.pipe(
            ofType(LeadAppLinkActionTypes.EditRequest),
            concatMap(({ Payload }) =>
                this.gatewayService
                    .Request({ LeadAppLinkEdit: Payload })
                    .then((res) => {
                        if (Object.keys(res?.Errors || {}).length === 0) {
                            return LeadAppLinkActionTypes.EditSuccess({
                                Payload: LeadAppLinkEntityFromBackend(res.LeadAppLinkEdit.Link),
                            });
                        } else {
                            throw res.Errors;
                        }
                    })
                    .catch((err) => {
                        return LeadAppLinkActionTypes.EditFailure({
                            Payload: {
                                ToasterMessage: ErrorCodes.Edit,
                                Errors: err,
                            },
                        });
                    }),
            ),
        ),
    );

    DeleteLeadAppLink$ = createEffect(() =>
        this.actions$.pipe(
            ofType(LeadAppLinkActionTypes.DeleteRequest),
            concatMap(({ Payload }) =>
                this.gatewayService
                    .Request({ LeadAppLinkDelete: Payload })
                    .then((res) => {
                        if (Object.keys(res?.Errors || {}).length === 0) {
                            return LeadAppLinkActionTypes.DeleteSuccess({
                                Payload: res.LeadAppLinkDelete.Link.Id,
                            });
                        } else {
                            throw res.Errors;
                        }
                    })
                    .catch((err) => {
                        return LeadAppLinkActionTypes.DeleteFailure({
                            Payload: {
                                ToasterMessage: ErrorCodes.Delete,
                                Errors: err,
                            },
                        });
                    }),
            ),
        ),
    );

    error$ = createEffect(() =>
        this.actions$.pipe(
            ofType(LeadAppLinkActionTypes.GetFailure, LeadAppLinkActionTypes.AddFailure, LeadAppLinkActionTypes.EditFailure, LeadAppLinkActionTypes.DeleteFailure),
            map(({ Payload }) =>
                BaseActionTypes.ErrorAction({
                    Payload: {
                        ToasterMessage: Payload.ToasterMessage,
                        Err: Payload.Errors,
                    },
                }),
            ),
        ),
    );

    constructor(private actions$: Actions<DaveActions>, private store$: Store<State>, private gatewayService: AppGatewayService) {}
}
